import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { finalize, map } from 'rxjs';
import {
  OrganizationSummary,
  OrganizationDetail,
  ChangedSubscription,
  SelectablePricingPlan,
  CreateOrganizationRequest,
  CreatedOrganization,
  InviteOrganisation,
  CompanySuggestion,
  AriregisterCompany,
  CompanySetupStatus,
  UpdateWebsiteRefresh,
  WebsiteRefreshState,
  MockDataBatchSummary,
  MockDataRemovalResult,
  StartCompanySetupRequest,
  SeededAdminCredentials,
  SeededAdminStatus,
  TeardownPreview,
  TeardownResult,
} from '../models/organization.types';
import { SearchUser } from '../models/search.types';
import { User } from '../models/auth.interface';
import { PricingDetail } from '../models/pricing.types';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  // Invite/delete/autocomplete live on the tenant API, not under /backoffice.
  // They are JwtGuard-only with the role check inside the Nest service.
  private baseApiUrl = environment.baseApiUrl;

  public errorMessage = signal<string | null>(null);

  public organizations = signal<OrganizationSummary[]>([]);
  public totalCount = signal<number>(0);
  public selectedOrgDetail = signal<OrganizationDetail | null>(null);
  public paginatedUsers = signal<User[]>([]);
  public totalUserCount = signal<number>(0);
  public pricingPlans = signal<SelectablePricingPlan[]>([]);
  public pricingPlansLoading = signal(false);

  loadOrganizations(startDate: Date, endDate: Date, page = 1, pageSize = 10, search?: string) {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (search?.trim()) params = params.set('search', search.trim());

    this.http
      .get<{
        organizations: OrganizationSummary[];
        totalCount: number;
      }>(`${this.apiUrl}/backoffice/organizations`, { params })
      .subscribe({
        next: (data) => {
          this.organizations.set(data.organizations);
          this.totalCount.set(data.totalCount);
        },
        error: (err) => this.errorMessage.set(err.message),
      });
  }

  loadOrganizationDetail(orgId: number, startDate: Date, endDate: Date, page = 1, pageSize = 10) {
    const params = new HttpParams()
      .set('organisationId', orgId.toString())
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    // The endpoint wraps the payload: { detail: {...}, totalUsers }.
    this.http
      .get<{
        detail: OrganizationDetail;
        totalUsers: number;
      }>(`${this.apiUrl}/backoffice/organizations/detail`, { params })
      .subscribe({
        next: (data) => {
          this.selectedOrgDetail.set(data.detail);
          this.paginatedUsers.set(data.detail.users as unknown as User[]);
          this.totalUserCount.set(data.totalUsers);
        },
        error: (err) => this.errorMessage.set(err.message),
      });
  }

  clearUserOnboardingEvents(userId: number) {
    return this.http.delete<{ deleted: number }>(
      `${this.apiUrl}/backoffice/organizations/user-onboarding-events`,
      { params: { userId: userId.toString() } },
    );
  }

  toggleInternal(organisationId: number, isInternal: boolean) {
    return this.http.put<{ id: number; isInternal: boolean }>(
      `${this.apiUrl}/backoffice/organizations/internal`,
      { organisationId, isInternal },
    );
  }

  updateSubscription(data: {
    organisationId: number;
    status: string;
    seats: number;
    currentPeriodEnd: Date;
    cancelAtPeriodEnd: boolean;
  }) {
    const body = {
      ...data,
      currentPeriodEnd: data.currentPeriodEnd.toISOString(),
    };

    return this.http.put(`${this.apiUrl}/backoffice/organizations/subscription`, body);
  }

  /**
   * The name of the plan the organisation's default subscription is on.
   *
   * Read from the pricing detail, which picks the default subscription the same way the
   * organisation detail does - that one carries no plan. The date range only scopes the usage
   * figures this ignores.
   */
  loadCurrentPlanName(organisationId: number, startDate: Date, endDate: Date) {
    const params = new HttpParams()
      .set('organisationId', organisationId.toString())
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http
      .get<PricingDetail>(`${this.apiUrl}/backoffice/pricing/detail`, { params })
      .pipe(map((detail) => detail.planName));
  }

  /**
   * Move the organisation's default subscription onto another active plan - in Stripe first when it
   * has a Stripe subscription, then locally. Seats and dates stay as they are.
   *
   * 409 when a downgrade or cancellation is already scheduled at period end (it would undo this),
   * 400 when the plan has no Stripe price for the subscription's billing period.
   */
  changePlan(organisationId: number, pricingPlanId: number) {
    return this.http.put<ChangedSubscription>(`${this.apiUrl}/backoffice/organizations/plan`, {
      organisationId,
      pricingPlanId,
    });
  }

  /**
   * Move the period end later. Only the end moves: a trial stays a trial and the usage window is not
   * reset.
   *
   * 400 when the date is not after the current end, 409 when the subscription is paid through
   * Stripe - only a trial can be extended there.
   */
  extendPeriod(organisationId: number, currentPeriodEnd: Date) {
    return this.http.put<ChangedSubscription>(`${this.apiUrl}/backoffice/organizations/period`, {
      organisationId,
      currentPeriodEnd: currentPeriodEnd.toISOString(),
    });
  }

  /**
   * Create an organisation on a customer's behalf: organisation, default subscription on the chosen
   * plan and the admin invite mail, all in one call. Left as a cold observable, unlike the loaders
   * above, because the caller has a form to keep disabled and errors to show inline - a 409 on a
   * duplicate name is something the operator fixes in the field, not a banner at the top of a page.
   */
  createOrganization(body: CreateOrganizationRequest) {
    return this.http.post<CreatedOrganization>(`${this.apiUrl}/backoffice/organizations`, body);
  }

  /** Fills the plan pickers on the creation form and the detail screen. Active plans only, cheapest first. */
  loadPricingPlans() {
    this.pricingPlansLoading.set(true);
    this.http
      .get<SelectablePricingPlan[]>(`${this.apiUrl}/backoffice/organizations/pricing-plans`)
      .pipe(finalize(() => this.pricingPlansLoading.set(false)))
      .subscribe({
        next: (plans) => this.pricingPlans.set(plans),
        error: (err: Error) => this.errorMessage.set(err.message),
      });
  }

  /**
   * Does this organisation have a generated admin account? Carries no password and writes no audit
   * row, so it is safe to ask whenever a screen renders - which is what lets the card offer
   * "Generate" or "Show password" without guessing.
   */
  loadSeededAdminStatus(organisationId: number) {
    return this.http.get<SeededAdminStatus>(
      `${this.apiUrl}/backoffice/organizations/seeded-admin/status`,
      { params: { organisationId: organisationId.toString() } },
    );
  }

  /**
   * Generate the organisation's admin account. 409 when it already has one - the caller shows that
   * as a message rather than as a failure, since the credentials are simply readable instead.
   */
  generateSeededAdmin(organisationId: number) {
    return this.http.post<SeededAdminCredentials>(
      `${this.apiUrl}/backoffice/organizations/seeded-admin`,
      { organisationId },
    );
  }

  /**
   * Read the generated credentials. Every call writes a SEEDED_ADMIN_VIEWED audit row on the API
   * side, so only call it when an operator has asked to see them - never on page load. 404 means
   * this organisation has no generated account yet.
   */
  loadSeededAdmin(organisationId: number) {
    return this.http.get<SeededAdminCredentials>(
      `${this.apiUrl}/backoffice/organizations/seeded-admin`,
      { params: { organisationId: organisationId.toString() } },
    );
  }

  /**
   * The organisation's mock data batches, newest first, removed ones included.
   *
   * Cheap and side-effect free, so the card asks it as it renders. Removed batches stay in the
   * answer on purpose: "somebody seeded this tenant and took it out again" is a question the list
   * exists to answer.
   */
  loadMockDataBatches(organisationId: number) {
    return this.http.get<MockDataBatchSummary[]>(
      `${this.apiUrl}/backoffice/organizations/mock-data`,
      { params: { organisationId: organisationId.toString() } },
    );
  }

  /**
   * Seed the organisation with a fresh batch of demo content.
   *
   * 403 unless the organisation is marked internal, and 409 when it has no generated admin account
   * to attribute the content to - which the caller shows as something to fix rather than a failure,
   * since the account is one click away on the same screen.
   *
   * A COMPLETED answer means every model was written; PARTIAL means some failed and the rows that
   * landed are still removable through the same batch.
   */
  seedMockData(organisationId: number) {
    return this.http.post<MockDataBatchSummary>(
      `${this.apiUrl}/backoffice/organizations/mock-data`,
      { organisationId },
    );
  }

  /** The weekly website refresh schedule and its recent runs. */
  loadWebsiteRefresh(organisationId: number) {
    return this.http.get<WebsiteRefreshState>(
      `${this.apiUrl}/backoffice/organizations/website-refresh`,
      {
        params: { organisationId: organisationId.toString() },
      },
    );
  }

  /** Save the schedule. Audited server-side; answers with the fresh state. */
  updateWebsiteRefresh(body: UpdateWebsiteRefresh) {
    return this.http.put<WebsiteRefreshState>(
      `${this.apiUrl}/backoffice/organizations/website-refresh`,
      body,
    );
  }

  /** Drop the page baseline; the next run takes the site as it is and becomes due at once. Audited. */
  resetWebsiteRefresh(organisationId: number) {
    return this.http.post<WebsiteRefreshState>(
      `${this.apiUrl}/backoffice/organizations/website-refresh/reset`,
      { organisationId },
    );
  }

  /** Remove one batch's rows. Irreversible - they are deleted, not archived. */
  removeMockDataBatch(organisationId: number, batchId: string) {
    return this.http.delete<MockDataRemovalResult>(
      `${this.apiUrl}/backoffice/organizations/mock-data`,
      {
        params: { organisationId: organisationId.toString(), batchId, confirm: 'true' },
      },
    );
  }

  /**
   * Where the organisation's company setup run has got to.
   *
   * Cheap and side-effect free, so the card asks as it renders and polls this while a run is in
   * flight. It is also where the API writes down what a finished run added up to, which is why the
   * card keeps polling until the answer stops saying PENDING rather than stopping at the first
   * non-pending job state.
   */
  loadCompanySetup(organisationId: number) {
    return this.http.get<CompanySetupStatus>(
      `${this.apiUrl}/backoffice/organizations/company-setup`,
      { params: { organisationId: organisationId.toString() } },
    );
  }

  /**
   * Start a run: crawl the prospect's website and generate a sales playbook and an ICP from it.
   *
   * Minutes of work once it starts, and it costs real LLM calls against this organisation, so the
   * card confirms before calling. Three 409s, none of them a dead end: no generated admin account,
   * a run already in flight, or a playbook and an ICP that already exist - the last meaning demo
   * content was seeded first and the job would generate nothing.
   */
  startCompanySetup(request: StartCompanySetupRequest) {
    return this.http.post<CompanySetupStatus>(
      `${this.apiUrl}/backoffice/organizations/company-setup`,
      request,
    );
  }

  /**
   * Abandon a run that is still going.
   *
   * Takes effect at the job's next phase boundary rather than immediately, and what it has already
   * spent is not recoverable - so the answer can still come back PENDING.
   */
  cancelCompanySetup(organisationId: number) {
    return this.http.delete<CompanySetupStatus>(
      `${this.apiUrl}/backoffice/organizations/company-setup`,
      { params: { organisationId: organisationId.toString(), confirm: 'true' } },
    );
  }

  /**
   * What tearing this organisation down would touch.
   *
   * Read-only and cheap, so the card asks as it renders and again after any change. The counts are
   * not an authorisation and are not remembered anywhere: the delete recomputes them from scratch
   * for the audit row.
   */
  getTeardownPreview(organisationId: number) {
    return this.http.get<TeardownPreview>(
      `${this.apiUrl}/backoffice/organizations/teardown/preview`,
      { params: { organisationId: organisationId.toString() } },
    );
  }

  /**
   * Tear the organisation down. Irreversible, and there is no restore.
   *
   * `confirmName` is the organisation's exact name, typed by the operator. It is checked server-side
   * too - the typing gate is what stands between a mis-clicked row and a deleted tenant, and a gate
   * that only exists in the browser is not a gate.
   *
   * 400 when the name does not match, 404 when the organisation is already gone.
   */
  tearDownOrganisation(organisationId: number, confirmName: string) {
    return this.http.delete<TeardownResult>(`${this.apiUrl}/backoffice/organizations/teardown`, {
      body: { organisationId, confirmName },
    });
  }

  /** Delete the generated account. Irreversible - the password cannot be recovered afterwards. */
  deleteSeededAdmin(organisationId: number) {
    return this.http.delete<{ deletedUserId: number }>(
      `${this.apiUrl}/backoffice/organizations/seeded-admin`,
      { params: { organisationId: organisationId.toString(), confirm: 'true' } },
    );
  }

  inviteOrganisation(data: InviteOrganisation) {
    return this.http.post<OrganizationSummary>(`${this.baseApiUrl}/organisation/invite`, data);
  }

  /**
   * The API proxies ariregister.rik.ee and returns its payload untouched, so the keys are the
   * register's own (`reg_code`, `legal_address`) — not ours. Same mapping the signup autocomplete
   * in the main app does (organisation-autocomplete.component.ts).
   */
  autocompleteCompany(query: string) {
    return this.http
      .get<{ data: AriregisterCompany[] }>(`${this.baseApiUrl}/organisation/autocomplete`, {
        params: { q: query },
      })
      .pipe(
        map((res) =>
          (res?.data ?? []).map<CompanySuggestion>((c) => ({
            name: c.name,
            registrationCode: c.reg_code != null ? String(c.reg_code) : undefined,
            address: c.legal_address ?? undefined,
          })),
        ),
      );
  }

  searchUsers(q: string) {
    const params = new HttpParams().set('q', q);
    return this.http.get<SearchUser[]>(`${this.apiUrl}/backoffice/organizations/users/search`, {
      params,
    });
  }

  resetDetail() {
    this.selectedOrgDetail.set(null);
    this.paginatedUsers.set([]);
    this.totalUserCount.set(0);
  }
}
